import React, { useEffect, useRef, useState } from 'react';
import { Link, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import './App.css';

// Assets
import i1 from './assets/i1.webp';
import i2 from './assets/i2.webp';
import i3 from './assets/i3.png';
import i4 from './assets/i4.png';
import logo from './assets/logo.png';
import mujaimg from './assets/muja.jpeg';
import resume from './assets/mujaali481gmail_resume.pdf';

// Pages
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Members from './pages/Members';
import Model3Section from './pages/Model3Section';
import MyCard from './pages/MyCard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Product from './pages/Product';
import SignUp from './pages/SignUp';
import Terms from './pages/Terms';
import Testimonials from './pages/Testimonials';

// Styles
import './pages/About.css';
import './pages/Contact.css';
import './pages/Module3.css';
import './pages/PrivacyPolicy.css';
import './pages/Terms.css';
import './pages/Testimonials.css';

// ScrollToTop component to handle route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

// Home component with HeroSection design
const Home = ({ showWhatsApp, setShowWhatsApp }) => {
  const aboutStatsRef = useRef(null);
  const aboutTitleRef = useRef(null);
  const [aboutTitleVisible, setAboutTitleVisible] = useState(false);
  // Check if animations have already been shown this session
  const [aboutStatsAnimated, setAboutStatsAnimated] = useState(() =>
    localStorage.getItem('aboutStatsAnimated') === 'true'
  );
  const [aboutStatsVisible, setAboutStatsVisible] = useState(() =>
    localStorage.getItem('aboutStatsAnimated') === 'true'
  );
  const [aboutStatsCount1, setAboutStatsCount1] = useState(() =>
    localStorage.getItem('aboutStatsAnimated') === 'true' ? 20 : 0
  );
  const [aboutStatsCount2, setAboutStatsCount2] = useState(() =>
    localStorage.getItem('aboutStatsAnimated') === 'true' ? 3 : 0
  );
  const [aboutStatsCount3, setAboutStatsCount3] = useState(() =>
    localStorage.getItem('aboutStatsAnimated') === 'true' ? 99 : 0
  );

  // Team member animation states
  const teamMemberRefs = useRef([]);
  const [teamMemberVisible, setTeamMemberVisible] = useState(Array(1).fill(false));

  // Mission Vision Values animation state
  const missionVisionRef = useRef(null);
  const [missionVisionVisible, setMissionVisionVisible] = useState(false);
  const [currentPersonaSet, setCurrentPersonaSet] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [threadVisible, setThreadVisible] = useState(false);
  const [threadBroken, setThreadBroken] = useState(false);

  const imageMap = {
    'i1.webp': i1,
    'i2.webp': i2,
    'i3.png': i3,
    'i4.png': i4,
  };

  const personaSets = [
    {
      persona1: {
        name: "AI Innovation",
        description: "Building intelligent systems with machine learning and deep learning frameworks. Creating solutions that learn, adapt, and deliver real business value through automation and insights.",
        photo: "i1.webp"
      },
      persona2: {
        name: "Web Development",
        description: "Crafting responsive, modern web applications with React, Node.js, and cutting-edge technologies. Delivering seamless user experiences with clean, maintainable code.",
        photo: "i2.webp"
      }
    },
    {
      persona1: {
        name: "Full-Stack Solutions",
        description: "End-to-end development expertise from database design to frontend interfaces. Building scalable architectures that handle real-world complexity with elegance and efficiency.",
        photo: "i3.png"
      },
      persona2: {
        name: "Problem Solving",
        description: "Passionate about tackling complex challenges with innovative approaches. Turning ideas into production-ready applications that make a meaningful impact.",
        photo: "i4.png"
      }
    }
  ];
  // HeroSection animation logic
  useEffect(() => {
    const timer = setTimeout(() => {
      const textItems = document.querySelectorAll('.rotating-text .text-item');
      const animatedDot = document.querySelector('.animated-dot');
      let currentIndex = 0;
      let rotationCount = 0;

      if (textItems.length === 0 || !animatedDot) return;

      // Clear all initial states
      textItems.forEach(item => {
        item.classList.remove('active', 'exit');
      });

      // Set initial state - first item active and dot positioned
      textItems[0].classList.add('active');
      animatedDot.classList.add('pos-1');

      const rotateText = () => {
        if (rotationCount >= 2) return; // Stop after 2 rotations (3 total states)

        // Exit current item (slides up and fades out)
        textItems[currentIndex].classList.add('exit');
        textItems[currentIndex].classList.remove('active');

        // Move to next item
        const previousIndex = currentIndex;
        currentIndex = (currentIndex + 1) % textItems.length;
        rotationCount++;

        // Move dot to next position
        animatedDot.classList.remove('pos-1', 'pos-2', 'pos-3');
        animatedDot.classList.add(`pos-${currentIndex + 1}`);

        // Trigger persona container change and thread break at the same time
        setThreadBroken(true);
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentPersonaSet((prev) => (prev + 1) % personaSets.length);
          setCycleCount(prev => prev + 1);

          setTimeout(() => {
            setIsTransitioning(false);
            // Rejoin thread effect
            setTimeout(() => {
              setThreadBroken(false);
            }, 300);
          }, 100);
        }, 500);

        // After exit animation, show next item
        setTimeout(() => {
          // Remove exit class from previous item only
          textItems[previousIndex].classList.remove('exit');

          // Add active class to current item (slides up from bottom)
          textItems[currentIndex].classList.add('active');
        }, 400);
      };

      // Start rotation after 3 seconds, then every 4 seconds
      const startTimer = setTimeout(() => {
        rotateText();
        const interval = setInterval(rotateText, 4000);

        // Clear interval after 2 rotations
        setTimeout(() => {
          clearInterval(interval);
          // Return to first text item and stay there
          setTimeout(() => {
            textItems.forEach(item => item.classList.remove('active', 'exit'));
            textItems[0].classList.add('active');
            animatedDot.classList.remove('pos-1', 'pos-2', 'pos-3');
            animatedDot.classList.add('pos-1');
            // Reset persona to first set
            setCurrentPersonaSet(0);
            setCycleCount(0);
          }, 500);
        }, 8500);
      }, 3000);

      return () => clearTimeout(startTimer);
    }, 500);

    return () => clearTimeout(timer);
  }, [personaSets.length]);

  // Thread visibility effect on page load
  useEffect(() => {
    const threadTimer = setTimeout(() => {
      setThreadVisible(true);
    }, 1000);

    return () => clearTimeout(threadTimer);
  }, []);


  // About Stats Animation Effect
  useEffect(() => {
    const aboutStatsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !aboutStatsAnimated) {
            setAboutStatsVisible(true);
            setAboutStatsAnimated(true);

            // Save animation state to localStorage so it runs once across visits
            localStorage.setItem('aboutStatsAnimated', 'true');

            // Animate count-up for stat 1 (20+)
            let count1 = 0;
            const target1 = 20;
            const increment1 = target1 / 60;
            const timer1 = setInterval(() => {
              count1 += increment1;
              if (count1 >= target1) {
                setAboutStatsCount1(target1);
                clearInterval(timer1);
              } else {
                setAboutStatsCount1(Math.floor(count1));
              }
            }, 30);

            // Animate count-up for stat 2 (3)
            let count2 = 0;
            const target2 = 3;
            const increment2 = target2 / 60;
            const timer2 = setInterval(() => {
              count2 += increment2;
              if (count2 >= target2) {
                setAboutStatsCount2(target2);
                clearInterval(timer2);
              } else {
                setAboutStatsCount2(Math.floor(count2));
              }
            }, 40);

            // Animate count-up for stat 3 (99%)
            let count3 = 0;
            const target3 = 99;
            const increment3 = target3 / 60;
            const timer3 = setInterval(() => {
              count3 += increment3;
              if (count3 >= target3) {
                setAboutStatsCount3(target3);
                clearInterval(timer3);
              } else {
                setAboutStatsCount3(Math.floor(count3));
              }
            }, 60);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (aboutStatsRef.current) {
      aboutStatsObserver.observe(aboutStatsRef.current);
    }

    return () => {
      if (aboutStatsRef.current) {
        aboutStatsObserver.unobserve(aboutStatsRef.current);
      }
    };
  }, [aboutStatsAnimated]);



  // Team Members Animation Effect
  useEffect(() => {
    const teamMemberObservers = teamMemberRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTeamMemberVisible(prev => {
                const newVisible = [...prev];
                newVisible[index] = true;
                return newVisible;
              });
            }
          });
        },
        { threshold: 0.5, rootMargin: '-10% 0px -10% 0px' }
      );

      observer.observe(ref);
      return observer;
    });

    return () => {
      teamMemberObservers.forEach((observer) => {
        if (observer) observer.disconnect();
      });
    };
  }, [teamMemberVisible]);

  // About Title Animation Effect
  useEffect(() => {
    const aboutTitleObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !aboutTitleVisible) {
            setAboutTitleVisible(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px -50px 0px'
      }
    );

    if (aboutTitleRef.current) {
      aboutTitleObserver.observe(aboutTitleRef.current);
    }

    return () => {
      if (aboutTitleRef.current) {
        aboutTitleObserver.unobserve(aboutTitleRef.current);
      }
    };
  }, [aboutTitleVisible]);


  // Mission Vision Values Animation Effect
  useEffect(() => {
    const missionVisionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setMissionVisionVisible(true);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '-10% 0px -10% 0px'
      }
    );

    if (missionVisionRef.current) {
      missionVisionObserver.observe(missionVisionRef.current);
    }

    return () => {
      if (missionVisionRef.current) {
        missionVisionObserver.unobserve(missionVisionRef.current);
      }
    };
  }, []);


  return (
    <div className="home-content">

      {/* Hero Section */}
      <section className="hero-section">
        {/* Top Left Corner Curve */}
        <div className={`corner-curve corner-curve-left ${threadVisible ? 'visible' : ''} ${threadBroken ? 'broken' : ''}`}>
          <svg className="curve-svg" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
            <path
              className="curve-path"
              d="M20,50 Q80,120 40,200 Q10,280 70,360"
              stroke="url(#curveGradientLeft)"
              strokeWidth="2"
              fill="none"
            />
            <defs>
              <linearGradient id="curveGradientLeft" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff69b4" />
                <stop offset="50%" stopColor="#ff1493" />
                <stop offset="100%" stopColor="#dc143c" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Top Right Corner Curve */}
        <div className={`corner-curve corner-curve-right ${threadVisible ? 'visible' : ''} ${threadBroken ? 'broken' : ''}`}>
          <svg className="curve-svg" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
            <path
              className="curve-path"
              d="M280,50 Q220,120 260,200 Q290,280 230,360"
              stroke="url(#curveGradientRight)"
              strokeWidth="2"
              fill="none"
            />
            <defs>
              <linearGradient id="curveGradientRight" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff69b4" />
                <stop offset="50%" stopColor="#ff1493" />
                <stop offset="100%" stopColor="#dc143c" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                <span className="static-text">Building Digital<br /><span className="">Experiences</span></span>
                <span className="rotating-text">
                  <span className="text-item text-item-1">
                    That Learn and<br />Adapt
                  </span>
                  <span className="text-item text-item-2">
                    That Solve Real<br />Problems
                  </span>
                  <span className="text-item text-item-3">
                    That Shape<br />the Future
                  </span>
                  <span className="animated-dot">.</span>
                </span>
              </h1>

              <p className="hero-description">
                Design and develop intelligent applications powered by AI and modern web technologies. From deep learning to full-stack solutions, delivering scalable, user-focused innovations that blend creativity with technology and transform ideas into reality.
              </p>
              <div className="hero-buttons">
                <button
                  className="hero-btn-get-started"
                  onClick={() => {
                    // Scroll to Model3Section instead of routing
                    const projectsSection = document.querySelector('.model3-section') ||
                      document.querySelector('.workforce-section') ||
                      document.querySelector('.next-section');
                    if (projectsSection) {
                      projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  View My Work
                </button>
              </div>
            </div>

            <div className="hero-personas" data-persona-set={currentPersonaSet}>
              <div className={`persona-container persona-container-1 ${isTransitioning ? 'slide-out' : ''}`}>
                <div className="persona-image-card persona-1">
                  <img src={imageMap[personaSets[currentPersonaSet].persona1.photo]} alt={personaSets[currentPersonaSet].persona1.name} className="persona-photo-img" />
                </div>
                <div className="persona-info-card">
                  <div className="persona-name">{personaSets[currentPersonaSet].persona1.name}</div>
                  <div className="persona-description">
                    {personaSets[currentPersonaSet].persona1.description}
                  </div>
                </div>
              </div>

              <div className={`persona-container persona-container-2 ${isTransitioning ? 'slide-out' : ''}`}>
                <div className="persona-image-card persona-2">
                  <img src={imageMap[personaSets[currentPersonaSet].persona2.photo]} alt={personaSets[currentPersonaSet].persona2.name} className="persona-photo-img" />
                </div>
                <div className="persona-info-card">
                  <div className="persona-name">{personaSets[currentPersonaSet].persona2.name}</div>
                  <div className="persona-description">
                    {personaSets[currentPersonaSet].persona2.description}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-background-elements">
          <div className="bg-circle bg-circle-1"></div>
          <div className="bg-circle bg-circle-2"></div>
        </div>
      </section>

      {/* Model 3 Section */}
      <Model3Section />



      {/* About Us Section */}
      <section id="about" className="about-section">
        <div className="about-background-elements">
          <div className="about-quote-icon about-quote-left">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" fill="#e0e7ff" />
            </svg>
          </div>
          <div className="about-quote-icon about-quote-right">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.57-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-4v-10h10z" fill="#e0e7ff" />
            </svg>
          </div>
        </div>
        <div className="about-container" ref={aboutTitleRef}>
          <p className={`about-subtitle ${aboutTitleVisible ? 'animate-up' : ''}`} style={{ color: '#888888' }}>ABOUT ME</p>
          <h2 className={`about-title ${aboutTitleVisible ? 'animate-up' : ''}`}>Turning ideas into interactive and intelligent experiences with code and AI.</h2>

          <div className={`about-stats moving-border ${aboutStatsVisible ? 'animate-up' : ''}`} ref={aboutStatsRef}>
            <div className="stat-item">
              <span className="stat-number">{aboutStatsCount1}+</span>
              <span className="stat-label">Projects Completed</span>
              <span className="stat-description">Real-world apps and AI solutions</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">Full-Stack & AI</span>
              <span className="stat-label">Expertise</span>
              <span className="stat-description">React, Next.js, Tailwind, Python, Deep Learning</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{aboutStatsCount3}%</span>
              <span className="stat-label">Dedication</span>
              <span className="stat-description">Passionate about building clean, functional, and creative solutions</span>
            </div>
          </div>

          <div className="team-members">
            <div ref={(el) => (teamMemberRefs.current[0] = el)}>
              <div className={`member-profile ${teamMemberVisible[0] ? 'animate-slide-left' : ''}`}>
                <img src={mujaimg} alt="Mujakath Ali M" className="member-img" />
                <h3 className="member-name">Mujakath Ali M</h3>
                <p className="member-title">Software Developer & AI/ML Engineer</p>
              </div>
              <div className={`member-content ${teamMemberVisible[0] ? 'animate-slide-diagonal' : ''}`}>
                <p>
                  Hey guys! I am Mujakath Ali M – a passionate Software Developer and AI/ML Engineer who loves turning ideas into intelligent, interactive, and impactful digital solutions. With strong expertise in deep learning, computer vision, natural language processing, and full-stack web development, I build applications that solve real-world problems while pushing the boundaries of technology.
                </p>
                <p>
                  Skilled in Python, TensorFlow, PyTorch, React, and modern development tools, I thrive on tackling complex challenges and creating solutions that are both innovative and efficient. My mission is to craft cutting-edge AI solutions and dynamic web experiences that inspire, empower, and deliver measurable impact.
                </p>
                <p>
                  Always curious, constantly learning, and driven by creativity, I aim to blend technology and imagination to build the next generation of smart applications.
                </p>
                <div className="bio-buttons">
                  <a href="https://linkedin.com/in/mujakathali" className="linkedin-link" target="_blank" rel="noopener noreferrer">Mujakath's LinkedIn</a>
                  <button className="resume-btn" onClick={() => {
                    // Open resume in new tab for viewing, and also provide download option
                    window.open(resume, '_blank');
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    View Resume
                  </button>
                  <button className="resume-btn download-btn" onClick={() => {
                    // Download resume PDF
                    const link = document.createElement('a');
                    link.href = resume;
                    link.download = 'Mujakath_Ali_Resume.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Download
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Product Section */}
      <Product />

      {/* Mission, Vision, Values - Full Width Section */}
      <section className="mission-vision-values-section" ref={missionVisionRef}>
        <div className="about-container">
          <div className={`mission-content ${missionVisionVisible ? 'slide-up' : ''}`}>
            <h2 className="section-title">My Mission</h2>
            <p className="mission-text">
              My mission is to leverage technology, creativity, and problem-solving to build solutions that make a real impact. I aim to combine my skills in AI, machine learning, and frontend development to create applications that are not only intelligent but also user-friendly and accessible. Every project I pursue is driven by a desire to learn, innovate, and contribute value to people's lives.
            </p>
          </div>

          <div className={`vision-content ${missionVisionVisible ? 'slide-up' : ''}`}>
            <h2 className="section-title">My Vision</h2>
            <p className="vision-text">
              My vision is to become a professional who bridges the gap between cutting-edge technology and real-world challenges. I see myself designing systems and applications that empower individuals, businesses, and communities to achieve more through automation, intelligence, and simplicity. In the long run, I aspire to grow into a leader who inspires innovation, collaboration, and lifelong learning.
            </p>
          </div>

          <div className={`values-content ${missionVisionVisible ? 'slide-up' : ''}`}>
            <h2 className="section-title">My Values</h2>
            <div className="values-grid">
              <div className="value-item">
                <h4>Innovation</h4>
                <p>
                  I believe in pushing boundaries and experimenting with new ideas to find better solutions.
                </p>
              </div>
              <div className="value-item">
                <h4>Continuous Learning</h4>
                <p>
                  I value curiosity and the drive to keep growing, adapting, and improving my skills.
                </p>
              </div>
              <div className="value-item">
                <h4>Integrity</h4>
                <p>
                  I believe in being honest, transparent, and responsible in all my work.
                </p>
              </div>
              <div className="value-item">
                <h4>Collaboration</h4>
                <p>
                  I value teamwork and shared success, knowing the best outcomes often come from working together.
                </p>
              </div>
              <div className="value-item">
                <h4>Impact</h4>
                <p>
                  I measure success not just by results, but by the positive difference my work creates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="whatsapp-float" onClick={() => setShowWhatsApp(!showWhatsApp)}>
        <div className="whatsapp-radar"></div>
        <div className="whatsapp-radar radar-2"></div>
        <div className="whatsapp-radar radar-3"></div>
        <div className="whatsapp-icon">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z" />
          </svg>
        </div>
      </div>

      {/* WhatsApp Chat Modal */}
      {showWhatsApp && (
        <div className="whatsapp-modal">
          <div className="whatsapp-header">
            <div className="whatsapp-profile">
              <img src={logo} alt="Portfolio" className="whatsapp-avatar" />
              <div className="whatsapp-info">
                <h4>Mujakath Ali</h4>
                <span>Full-Stack & AI Developer</span>
              </div>
            </div>
            <button className="whatsapp-close" onClick={() => setShowWhatsApp(false)}>✕</button>
          </div>
          <div className="whatsapp-body">
            <div className="whatsapp-message">
              <p className="typing-text">Hi! Thanks for visiting my portfolio. Feel free to reach out if you'd like to discuss projects, opportunities, or just connect!</p>
              <span className="message-time">now</span>
            </div>
          </div>
          <div className="whatsapp-footer">
            <button
              className="start-chat-btn"
              onClick={() => window.open('https://wa.me/918681062029', '_blank')}
            >
              ▶ START CHAT
            </button>
          </div>
        </div>
      )}


    </div>
  );
};

// Main App component with routing
function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleWhatsApp = () => {
    setShowWhatsApp(!showWhatsApp);
  };


  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        {/* Navigation */}
        <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-logo" onClick={() => {
              // Scroll to top of page
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              <img src={logo} alt="Mobius Logo" className="logo-img" />
              <span className="logo-text">Portfolio</span>
            </Link>

            <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
              <Link to="/" className="nav-link" onClick={(e) => {
                setIsMenuOpen(false);
                // Scroll to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
                Home
              </Link>
              <a href="#about" className="nav-link" onClick={(e) => {
                setIsMenuOpen(false);
                // If we're not on home page, first navigate to home, then scroll to about
                if (window.location.pathname !== '/') {
                  e.preventDefault();
                  // Use React Router to navigate to home first
                  window.location.href = '/#about';
                }
              }}>
                About Me
              </a>
              <Link to="/projects" className="nav-link" onClick={(e) => {
                setIsMenuOpen(false);
                // If we're on home page, scroll to projects section instead of routing
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  const projectsSection = document.querySelector('.product-page') ||
                    document.querySelector('.workforce-section') ||
                    document.querySelector('.next-section');
                  if (projectsSection) {
                    projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }
                // If we're on other pages, let it route normally to /projects
              }}>
                Projects
              </Link>
              <Link to="/contact" className="nav-link" onClick={() => setIsMenuOpen(false)}>
                Contact
              </Link>
            </div>

            <div className={`nav-toggle ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
              <span className="bar"></span>
              <span className="bar"></span>
              <span className="bar"></span>
            </div>
          </div>
        </nav>

        {/* Routes */}
        <Routes>
          <Route path="/" element={
            <>
              <Home showWhatsApp={showWhatsApp} setShowWhatsApp={setShowWhatsApp} />
            </>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/members" element={<Members />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/projects" element={<Product />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/mycard" element={<MyCard />} />
        </Routes>

        {/* WhatsApp Floating Button - appears on all pages */}
        <div className="whatsapp-float" onClick={toggleWhatsApp}>
          <div className="whatsapp-radar radar-1"></div>
          <div className="whatsapp-radar radar-2"></div>
          <div className="whatsapp-radar radar-3"></div>
          <div className="whatsapp-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.58 6.02l-1.677 6.127L6.263 22.46a11.89 11.89 0 005.787 1.49h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0012.05.001" />
            </svg>
          </div>
        </div>

        {/* WhatsApp Message Container */}
        {showWhatsApp && (
          <div className="whatsapp-message-container">
            <div className="whatsapp-message-header">
              <img src={logo} alt="Mobius" className="whatsapp-msg-avatar" />
              <div className="whatsapp-msg-info">
                <h4>Mujakath Ali</h4>
                <span>Software & Ai engineer</span>
              </div>
              <button className="whatsapp-msg-close" onClick={toggleWhatsApp}>×</button>
            </div>
            <div className="whatsapp-message-body">
              <p className="typing-message">Hi! Let's connect and discuss how we can work together. I'm available for freelance projects, collaborations, or full-time opportunities.</p>
              <span className="message-time">now</span>
            </div>
            <div className="whatsapp-message-footer">
              <button
                className="start-chat-btn"
                onClick={() => window.open('https://wa.me/918681062029', '_blank')}
              >
                ▶ START CHAT
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="footer-container">
            <div className="footer-content">
              <div className="footer-left">
                <div className="footer-logo">
                  <img src={logo} alt="Portfolio" className="footer-logo-img" />
                  <div className="footer-brand">
                    <span className="footer-brand-name">Mujakath Ali M</span>
                    <span className="footer-tagline">Software Developer & AI Engineer</span>
                  </div>
                </div>
                <div className="footer-social">
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                  </a>
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                  </a>
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </a>
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zm13.202-.93c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-5.454-.972c0-.84-.687-1.522-1.522-1.522-.418 0-.796.165-1.071.435-1.056-.766-2.515-1.26-4.129-1.306l.705-3.309 2.294.484c.003.562.459 1.017 1.022 1.017.564 0 1.021-.455 1.021-1.017s-.457-1.021-1.021-1.021c-.417 0-.774.252-.929.609l-2.542-.537c-.11-.023-.219.038-.262.145l-.784 3.683c-1.673.041-3.178.537-4.257 1.312-.275-.27-.653-.435-1.071-.435-.835 0-1.522.682-1.522 1.522 0 .571.312 1.07.771 1.335-.04.191-.062.387-.062.586 0 2.977 3.472 5.389 7.75 5.389s7.75-2.412 7.75-5.389c0-.199-.023-.395-.063-.586.459-.265.771-.764.771-1.335zm-2.905 3.957c-.307-.305-.733-.458-1.158-.458-.425 0-.851.153-1.158.458l-.157.156c-.298.296-.777.296-1.075 0l-.157-.156c-.307-.305-.733-.458-1.158-.458-.425 0-.851.153-1.158.458-.305.307-.305.804 0 1.111.307.305.733.458 1.158.458.425 0 .851-.153 1.158-.458l.157-.156c.298-.296.777-.296 1.075 0l.157.156c.307.305.733.458 1.158.458.425 0 .851-.153 1.158-.458.305-.307.305-.804 0-1.111z" />
                    </svg>
                  </a>
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 2.096.547 4.142 1.58 6.02l-1.677 6.127L6.263 22.46a11.89 11.89 0 005.787 1.49h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0012.05.001" />
                    </svg>
                  </a>
                  <a href="#" className="social-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14.238 15.348c.085-.084.085-.221 0-.306-.465-.462-1.194-.687-2.231-.687l-.008-.002-.008.002c-1.036 0-1.766.225-2.231.687-.085.085-.085.222 0 .306.084.085.222.085.307 0 .379-.377 1.008-.561 1.924-.561l.008-.002.008.002c.916 0 1.545.184 1.924.561.085.085.223.085.307 0zm-3.44-2.418c0-.507-.414-.919-.922-.919-.509 0-.923.412-.923.919 0 .506.414.918.923.918.508.001.922-.411.922-.918zm13.202-.93c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-5.454-.972c0-.84-.687-1.522-1.522-1.522-.418 0-.796.165-1.071.435-1.056-.766-2.515-1.26-4.129-1.306l.705-3.309 2.294.484c.003.562.459 1.017 1.022 1.017.564 0 1.021-.455 1.021-1.017s-.457-1.021-1.021-1.021c-.417 0-.774.252-.929.609l-2.542-.537c-.11-.023-.219.038-.262.145l-.784 3.683c-1.673.041-3.178.537-4.257 1.312-.275-.27-.653-.435-1.071-.435-.835 0-1.522.682-1.522 1.522 0 .571.312 1.07.771 1.335-.04.191-.062.387-.062.586 0 2.977 3.472 5.389 7.75 5.389s7.75-2.412 7.75-5.389c0-.199-.023-.395-.063-.586.459-.265.771-.764.771-1.335zm-2.905 3.957c-.307-.305-.733-.458-1.158-.458-.425 0-.851.153-1.158.458l-.157.156c-.298.296-.777.296-1.075 0l-.157-.156c-.307-.305-.733-.458-1.158-.458-.425 0-.851.153-1.158.458-.305.307-.305.804 0 1.111.307.305.733.458 1.158.458.425 0 .851-.153 1.158-.458l.157-.156c.298-.296.777-.296 1.075 0l.157.156c.307.305.733.458 1.158.458.425 0 .851-.153 1.158-.458.305-.307.305-.804 0-1.111z" />
                    </svg>
                  </a>
                </div>

                <div className="footer-navigation">
                  <h4>Quick Navigation</h4>
                  <div className="footer-nav-links">
                    <Link to="/" className="footer-nav-link">Home</Link>
                    <Link to="/#about" className="footer-nav-link" onClick={(e) => {
                      if (window.location.pathname !== '/') {
                        e.preventDefault();
                        window.location.href = '/#about';
                      }
                    }}>About Me</Link>
                    <Link to="/projects" className="footer-nav-link" onClick={(e) => {
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        const projectsSection = document.querySelector('.product-page');
                        if (projectsSection) {
                          projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }
                    }}>Projects</Link>
                    <Link to="/contact" className="footer-nav-link">Contact</Link>
                  </div>
                </div>

              </div>

            </div>

            <div className="footer-bottom">
              <div className="footer-company-info">
                <h4 className="company-name">Contact Information</h4>
                <div className="company-details">
                  <div className="company-address">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                    <span>Tamil Nadu, India</span>
                  </div>
                  <div className="company-phone">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                    </svg>
                    <span>+91 86810 62029</span>
                  </div>
                  <div className="company-email">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                    <span>mujaali481@gmail.com</span>
                  </div>
                </div>
              </div>

              <div className="footer-copyright">
                <div className="footer-social-bottom">
                  <a href="https://reddit.com/u/mujakathali" className="social-icon-bottom reddit" target="_blank" rel="noopener noreferrer">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14.238 15.348c.085-.084.085-.221 0-.306-.465-.462-1.194-.687-2.231-.687l-.008-.002-.008.002c-1.036 0-1.766.225-2.231.687-.085.085-.085.222 0 .306.084.085.222.085.307 0 .379-.377 1.008-.561 1.924-.561l.008-.002.008.002c.916 0 1.545.184 1.924.561.085.085.223.085.307 0zm-3.44-2.418c0-.507-.414-.919-.922-.919-.509 0-.923.412-.923.919 0 .506.414.918.923.918.508.001.922-.411.922-.918zm13.202-.93c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-5.454-.972c0-.84-.687-1.522-1.522-1.522-.418 0-.796.165-1.071.435-1.056-.766-2.515-1.26-4.129-1.306l.705-3.309 2.294.484c.003.562.459 1.017 1.022 1.017.564 0 1.021-.455 1.021-1.017s-.457-1.021-1.021-1.021c-.417 0-.774.252-.929.609l-2.542-.537c-.11-.023-.219.038-.262.145l-.784 3.683c-1.673.041-3.178.537-4.257 1.312-.275-.27-.653-.435-1.071-.435-.835 0-1.522.682-1.522 1.522 0 .571.312 1.07.771 1.335-.04.191-.062.387-.062.586 0 2.977 3.472 5.389 7.75 5.389s7.75-2.412 7.75-5.389c0-.199-.023-.395-.063-.586.459-.265.771-.764.771-1.335zm-2.905 3.957c-.307-.305-.733-.458-1.158-.458-.425 0-.851.153-1.158.458l-.157.156c-.298.296-.777.296-1.075 0l-.157-.156c-.307-.305-.733-.458-1.158-.458-.425 0-.851.153-1.158.458-.305.307-.305.804 0 1.111.307.305.733.458 1.158.458.425 0 .851-.153 1.158-.458l.157-.156c.298-.296.777-.296 1.075 0l.157.156c.307.305.733.458 1.158.458.425 0 .851-.153 1.158-.458.305-.307.305-.804 0-1.111z" />
                    </svg>
                  </a>
                  <a href="https://linkedin.com/in/mujakathali" className="social-icon-bottom linkedin" target="_blank" rel="noopener noreferrer">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                </div>
                <div className="copyright-text">
                  <span>© 2025 Mujakath Ali M. All rights reserved.</span>
                  <div className="footer-legal-links">
                    <Link to="/privacy-policy">Privacy Policy</Link>
                    <Link to="/terms">Terms & Conditions</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
